/**
 * Thrivia Embed SDK: mount discovery and the mount registry.
 *
 * Purpose: turn an organizer's HTML into a typed list of things we are responsible for. Everything
 * downstream (the batched read in `api.ts`, the renderers in Task 5, the overlay in Task 6) works
 * off `MountRecord` and never re-queries the DOM for mounts.
 *
 * What counts as a mount:
 *
 * | Selector                                                   | Mode          | Data          |
 * |------------------------------------------------------------|---------------|---------------|
 * | `[data-thrivia-event]`                                      | `data-thrivia-mode`, default `card` | batched read |
 * | `[data-thrivia-creator]`                                    | always `list` | creator read  |
 * | `[data-thrivia-action="checkout"][data-thrivia-event-id]`   | `button`      | batched read  |
 * | `a[href*=thrivia]` when `data-thrivia-autolink` is set      | `button`      | only a `/e/<id>` permalink |
 *
 * The third row is the compatibility contract and it is the one that is easy to miss. Legacy
 * snippets that are live on organizer websites right now use a DIFFERENT attribute
 * (`data-thrivia-event-id`) and a different selector from everything the SDK generates today. The
 * loader must claim them, and must stamp `BOUND_ATTR` on every element it binds a handler to,
 * because that stamp is the entire handover protocol with `public/embed/checkout-button.js`: the
 * shim keeps handling any tagged CTA that is NOT stamped. Skip the legacy selector and nothing
 * breaks loudly, every live legacy button simply keeps the old behaviour forever and never reaches
 * the overlay. Stamp something we did not bind and the click is lost entirely.
 *
 * The registry is keyed by element, so a rescan after a `MutationObserver` fires cannot register
 * the same element twice, cannot bind a second click handler to it, and cannot re-request its data.
 *
 * Rendering is NOT here. `MountRenderer` is the interface Task 5 implements; this module only
 * discovers, binds and stamps.
 *
 * No runtime dependencies, DOM only, ES2019 output target.
 */

import type { EmbedEventCard } from "./api";
import { BOUND_ATTR, DT, readAttr, readFlag } from "./origin";

/** The surfaces from spec section 6. Matches the backend's ALLOWED_SURFACES exactly. */
export type MountMode = "card" | "button" | "list" | "page" | "bar";

/**
 * What a rendered CTA does when the visitor clicks it.
 *
 * `overlay` opens checkout in place, over the organizer's own page; `link` sends the visitor to
 * the canonical event page in a new tab, which is what every rendered CTA did before this
 * attribute existed. The organizer chooses per mount with `data-thrivia-cta`.
 *
 * The DEFAULT is `overlay`, and that is a behaviour change rather than a new option with a safe
 * default. It is safe to make because the embed program has never shipped: no organizer has
 * pasted a snippet, so there is no installed base whose CTA silently changes meaning. Keeping the
 * old default would have meant every organizer who wants what the incumbent widgets do has to
 * find an attribute to opt into it.
 *
 * `button` mounts ignore it: opening checkout is the entire purpose of that surface, and a button
 * that navigates instead is just the organizer's own link with our bytes on top of it.
 */
export type CtaMode = "overlay" | "link";

/**
 * Per-mount presentation, read off the organizer's own attributes.
 *
 * Colours and labels rather than a stylesheet, because the alternative organizers actually reach
 * for is `!important` against our classes, and a rule we lose to is worse than a value we were
 * handed. Everything here is UNTRUSTED markup on somebody else's page: colours are matched
 * against a hex pattern, hrefs against the same `HTTP_RE` every other URL in the bundle takes,
 * and labels are length-capped and only ever reach the DOM through `textContent`.
 *
 * Null means "we were told nothing", which is distinct from an empty string and is what lets a
 * renderer fall back to its own default rather than drawing a blank button.
 */
export interface MountTheme {
  /** Primary button background. */
  bg: string | null;
  /** Primary button text colour. */
  fg: string | null;
  /** Primary button label, replacing the surface's own copy. */
  label: string | null;
  /** `data-thrivia-radius="sharp"`: square the corners the bundle otherwise rounds. */
  sharp: boolean;
  /** Secondary action label. Both this and `actionHref` are required for it to render. */
  actionLabel: string | null;
  /** Secondary action destination, e.g. the itinerary PDF. */
  actionHref: string | null;
  /** Secondary action background. */
  actionBg: string | null;
  /** `data-thrivia-price="hide"`: draw the surface without its price line. */
  hidePrice: boolean;
}

/**
 * Where a mount is in its lifecycle. Task 5's renderers switch on this.
 *
 * `linked` is terminal and belongs to exactly one case: an autolinked anchor whose href carried no
 * event id. There is nothing to request and nothing to draw, so it must not sit at `pending`
 * forever on an organizer's own link, where a later pending-state rule (a skeleton, a shimmer)
 * would repaint markup we promised not to touch.
 *
 * `missing` and `error` are deliberately distinct even though they render the same way today.
 * `missing` means the API answered and had no such event, which is an organizer typo or an
 * unpublished event and is worth a beacon. `error` means we never got an answer at all, which is
 * ours to fix and cannot be beaconed because there is no session to beacon against.
 */
export type MountState = "pending" | "loading" | "ready" | "missing" | "error" | "linked";

/**
 * One thing on the page the loader is responsible for.
 *
 * This is the handoff to Task 5. Renderers receive a record, read `state`, `card`/`items` and
 * `el`, and write into `el`. They must never remove the baked permalink anchor: it is the
 * organizer's guaranteed-working fallback and the whole failure posture rests on it surviving.
 */
export interface MountRecord {
  /** Stable for the page's lifetime, assigned in discovery order. Debugging and test identity. */
  readonly id: number;
  readonly mode: MountMode;
  /** The organizer's element. For `button` mounts this may be their own styled CTA. */
  readonly el: HTMLElement;
  /**
   * ParentEvent id for an event mount; null for a list mount, and null for an autolinked anchor
   * whose href was not a `/e/<id>` permalink (a canonical `/@handle/slug` link carries no id).
   */
  readonly eventId: string | null;
  /** Creator username for a list mount; null otherwise. */
  readonly creator: string | null;
  /** `data-limit` on a list mount, already defaulted; null on every other mode. */
  readonly limit: number | null;
  /** True when this element was matched by the LEGACY `data-thrivia-action="checkout"` selector. */
  readonly legacy: boolean;
  /** True for an existing page link upgraded by the opt-in `data-thrivia-autolink`. */
  readonly autolink: boolean;
  /** True when this mount must send zero beacons: script-level or per-mount preview. */
  readonly preview: boolean;
  /** `data-thrivia-cta`: whether a rendered CTA opens checkout in place or navigates. */
  readonly cta: CtaMode;
  /** Colours, labels and the secondary action this mount was configured with. */
  readonly theme: MountTheme;
  /** True once a click handler has been attached and BOUND_ATTR stamped on `el`. */
  bound: boolean;
  state: MountState;
  /**
   * The attribution session that served THIS mount's data, or null.
   *
   * Per mount rather than global because a page with both a card and a list makes two requests and
   * each response carries its own sid, minted with its own surface. A beacon sent under the other
   * response's sid would be attributed to the wrong surface.
   */
  sid: string | null;
  /** The card for an event mount once loaded. */
  card: EmbedEventCard | null;
  /** The cards for a list mount once loaded, already truncated to `limit`. */
  items: EmbedEventCard[] | null;
  /** True once an impression has been accounted for, whether or not a beacon actually left. */
  impressed: boolean;
  /**
   * The `page` surface's iframe for this mount, once it has one.
   *
   * A renderer-owned field on a shared record, which is a deliberate exception rather than an
   * oversight. The page surface has to answer two questions from two directions: "which frame
   * belongs to this record" when its data lands, and "which record owns the window that just sent
   * a `resize`" when a message arrives. A module-level list answers the second on its own; this
   * answers the first without a second lookup structure that would have to be kept in step with the
   * pruning this file already does. Optional and never initialised, so it costs nothing on every
   * other mount, and the surface's own liveness timer releases it when the frame is declared dead.
   */
  frame?: HTMLIFrameElement;
  /**
   * True once this mount's `page` frame has said ANYTHING on the bus.
   *
   * The liveness flag for the page surface, on the record for the same reason `frame` is: the
   * answer is needed from two directions, by a message that has to mark the record owning the
   * window it came from, and by a timer that has to ask whether this record's frame ever spoke.
   * Silence past the frame timeout means the browser refused to frame us, which on an unregistered
   * organizer domain is now the ordinary case rather than an exotic one, and the surface renders a
   * message saying so instead of leaving a permanent empty box. Optional and never initialised.
   */
  live?: boolean;
  /**
   * Takes the "could not load here" panel back off this mount's frame, when one is covering it.
   *
   * The page surface's liveness verdict is provisional, so the thing that undoes it has to be
   * reachable from the message that overturns it. Set by the timer when it covers the frame, called
   * and cleared by the first message that frame sends. On the record for the same reason `frame` and
   * `live` are: the timer and the message reach the same mount from two directions. Optional and
   * never initialised.
   */
  uncover?: () => void;
  /**
   * The `bar` surface's node, which lives on `document.body` rather than inside the mount.
   *
   * A renderer-owned field for the same reason `frame` is one. The bar is pinned to the viewport,
   * so it cannot be a child of the organizer's mount div without inheriting whatever overflow,
   * transform or stacking context that div happens to sit in - any one of which silently turns
   * `position:fixed` into `position:absolute` and drops the bar somewhere down the page. Rendering
   * it on the body means the renderer is the only thing that knows where its own node went, and
   * teardown has to be able to find it again. Optional and never initialised.
   */
  barNode?: HTMLElement;
  /**
   * The bar's schema.org `<script>` in the host's `<head>`, for the same reason as `barNode`: it
   * lives outside the mount, so the renderer must be able to find it again to take it down.
   */
  ldNode?: HTMLElement;
}

/**
 * What Task 5 implements, one per mode, registered in `index.ts`.
 *
 * `mount` runs once at discovery, before any data exists, and is where a skeleton reserves height
 * so the organizer's CLS score is not ours to damage. `update` runs on every state change.
 * Both are called inside a try/catch: a renderer that throws must not take the page's other mounts
 * down with it.
 */
export interface MountRenderer {
  mount?(record: MountRecord): void;
  update(record: MountRecord): void;
  /**
   * Called once when the record is pruned because its element left the document.
   *
   * A single-page-application host replaces its containers on every route change, so a mount is not
   * guaranteed to outlive the page. Anything a renderer attached OUTSIDE `record.el` (a
   * ResizeObserver, a window listener, an iframe message handler for the page surface) has to be
   * released here or it leaks for as long as the tab is open. Anything inside `record.el` goes with
   * the element and needs nothing.
   *
   * **Guard your own teardown.** Unlike `mount` and `update`, which `index.ts` calls inside a
   * per-record try/catch, `teardown` runs inside the SCAN pass's single try. A throw here therefore
   * aborts discovery for that whole pass: the mounts the host added in the same mutation burst are
   * not found, and nothing retries until the next mutation. Wrap the body.
   */
  teardown?(record: MountRecord): void;
}

/**
 * The live registry.
 *
 * `list` is discovery order and is published as `window.__thrivia.mounts`, so it is mutated in
 * place and never reassigned. `byEl` is what makes a rescan idempotent. `nextId` is separate from
 * `list.length` because pruning shortens the list and reused ids would collide.
 */
export interface MountRegistry {
  list: MountRecord[];
  byEl: Map<Element, MountRecord>;
  nextId: number;
}

export interface ScanOptions {
  autolink: boolean;
  preview: boolean;
  /**
   * Attached to every bound element. `index.ts` owns the behaviour; Task 6 replaces its body with
   * the overlay. Whatever it does, the element is stamped with BOUND_ATTR the moment it is
   * attached, so the shim stands down for that element and exactly one handler is ever live.
   */
  onClick(record: MountRecord, event: MouseEvent): void;
}

/** Mount divs the SDK's own snippets generate. */
const MOUNT_SELECTOR = "[" + DT + "event],[" + DT + "creator]";

/**
 * The legacy snippet's selector, unchanged since the original 20 line `checkout-button.js`.
 *
 * `data-thrivia-event-id` is required in the selector as well as the action attribute: without an
 * event id there is nothing for the overlay to open, and the shim's own "ignore a tagged element
 * with no href" path already covers that element correctly.
 */
const LEGACY_SELECTOR = "[" + DT + 'action="checkout"][' + DT + "event-id]";

/**
 * Anything already spoken for, used to keep autolink off our own markup.
 *
 * The `.thrivia-r` term is the one that is easy to miss and it was found by the fixture, not by
 * reasoning. Every node this bundle renders is rooted in an element carrying that class, and while
 * cards and pills live INSIDE a mount (so the first three terms already cover them), the checkout
 * overlay is appended to `document.body`. Its degraded "Continue on thrivia.io" link is therefore a
 * plain thrivia.io anchor sitting loose in the document, and on a page with autolink on, the next
 * rescan claimed it: the loader bound its own escape hatch. With a permalink fallback that is a
 * trap rather than an untidiness, because clicking the link would re-open the overlay that had just
 * failed, and the visitor would never reach thrivia.io at all.
 */
const CLAIMED_SELECTOR =
  "[" + DT + "event],[" + DT + "creator],[" + DT + "action],.thrivia-r";

/** Hosts whose links autolink will upgrade. The apex and www both, plus any future subdomain. */
const THRIVIA_HOST_RE = /(^|\.)thrivia\.io$/i;

/**
 * The one existing link shape that carries an event id: the permalink `…/e/<ObjectId>`.
 *
 * Autolink is handed an anchor and nothing else, and the checkout overlay is keyed by event id, so
 * an upgraded link can only become an overlay opener when its href reveals the id. A permalink does
 * (`/e/68f0…`); the canonical `/@handle/slug` does not, and there is no public endpoint that
 * resolves a slug to an id, so those stay ordinary links that we merely count. See `index.ts`.
 */
const PERMALINK_RE = /\/e\/([0-9a-f]{24})(?:[/?#]|$)/i;

/** Spec section 6: the list surface renders up to `data-limit`, default 10. */
const DEFAULT_LIST_LIMIT = 10;
/** Matches MAX_LIST_LIMIT in the backend's EmbedService, which clamps anything larger. */
const MAX_LIST_LIMIT = 500;

/**
 * The mount modes, as a SET rather than a lookup object.
 *
 * A `Record<string, MountMode>` made membership a truthy test, and `MODES["constructor"]` is a
 * function off Object.prototype - so `data-thrivia-mode="constructor"` became a MountMode with no
 * renderer behind it and the organizer got a blank box. A Set has no prototype entries to inherit,
 * which makes the invariant structural instead of something the next value added has to remember.
 */
const MODES = new Set<string>(["card", "button", "list", "page", "bar"]);

/** Same reasoning as MODES: a Set, so `constructor` is a miss rather than an inherited hit. */
const CTA_MODES = new Set<string>(["overlay", "link"]);

/**
 * Longest label we will draw. Generous for a button, short enough that a hostile attribute cannot
 * push a wall of text onto the organizer's page under our class names.
 */
const MAX_LABEL = 40;

/** Colours are hex only. Named colours, `rgb()` and `var()` all open a door we gain nothing from. */
const HEX_RE = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** Same scheme guard the card CTA takes. Duplicated rather than imported: `card.ts` imports us. */
const URL_RE = /^https?:\/\//i;

function readCta(el: Element): CtaMode {
  const raw = (el.getAttribute(DT + "cta") || "").trim().toLowerCase();
  return CTA_MODES.has(raw) ? (raw as CtaMode) : "overlay";
}

function readColor(el: Element, name: string): string | null {
  const raw = readAttr(el, DT + name);
  return raw && HEX_RE.test(raw) ? raw : null;
}

function readLabel(el: Element, name: string): string | null {
  const raw = readAttr(el, DT + name);
  return raw ? raw.slice(0, MAX_LABEL) : null;
}

function readTheme(el: Element): MountTheme {
  const href = readAttr(el, DT + "action-href");
  return {
    bg: readColor(el, "color"),
    fg: readColor(el, "text-color"),
    label: readLabel(el, "label"),
    sharp: (readAttr(el, DT + "radius") || "").toLowerCase() === "sharp",
    actionLabel: readLabel(el, "action-label"),
    actionHref: href && URL_RE.test(href) ? href : null,
    actionBg: readColor(el, "action-color"),
    hidePrice: (readAttr(el, DT + "price") || "").toLowerCase() === "hide",
  };
}

function readMode(el: Element, fallback: MountMode): MountMode {
  const raw = (el.getAttribute(DT + "mode") || "").trim().toLowerCase();
  return MODES.has(raw) ? (raw as MountMode) : fallback;
}

function readLimit(el: Element): number {
  const raw = Number(readAttr(el, "data-limit"));
  if (!isFinite(raw) || raw < 1) return DEFAULT_LIST_LIMIT;
  return Math.min(Math.floor(raw), MAX_LIST_LIMIT);
}

/** True when this anchor points at a Thrivia URL that autolink should upgrade. */
function isThriviaLink(anchor: HTMLAnchorElement): boolean {
  try {
    return THRIVIA_HOST_RE.test(new URL(anchor.href, document.baseURI).hostname);
  } catch {
    return false;
  }
}

/** The event id inside a permalink href, or null for every other Thrivia URL shape. */
function linkEventId(anchor: HTMLAnchorElement): string | null {
  const match = PERMALINK_RE.exec(anchor.href);
  return match ? match[1] : null;
}

export function createRegistry(): MountRegistry {
  return { list: [], byEl: new Map(), nextId: 0 };
}

/** `isConnected` is universal on modern engines; `document.contains` covers anything older. */
function isDetached(el: Element): boolean {
  return "isConnected" in el ? !el.isConnected : !document.contains(el);
}

/**
 * Drops records whose element has left the document, returning them so the caller can release
 * whatever it attached (impression observers, renderer teardown).
 *
 * Without this the registry only ever grows. A single-page-application host that replaces its
 * container on each route change leaves one dead record per navigation, each still holding a strong
 * reference to a detached element and its rendered subtree, and each still counted by anything that
 * walks `mounts`. It is not only a leak: the replacement container is a genuinely new element, so it
 * is rediscovered and refetched, and the dead record's data would otherwise be indistinguishable
 * from the live one's.
 *
 * `list` is mutated in place because it is the same array published as `window.__thrivia.mounts`.
 */
export function pruneMounts(registry: MountRegistry): MountRecord[] {
  const removed: MountRecord[] = [];
  const kept: MountRecord[] = [];

  for (let i = 0; i < registry.list.length; i++) {
    const record = registry.list[i];
    if (isDetached(record.el)) {
      registry.byEl.delete(record.el);
      removed.push(record);
    } else {
      kept.push(record);
    }
  }

  if (removed.length > 0) {
    registry.list.length = 0;
    for (let i = 0; i < kept.length; i++) registry.list.push(kept[i]);
  }

  return removed;
}

/**
 * Scans `root` for mounts not already registered and returns only the NEW ones.
 *
 * Returning only new records is what lets `index.ts` request data for exactly the mounts that need
 * it after a `MutationObserver` rescan, instead of re-fetching the whole page every time a host
 * framework touches the DOM. Task 5's renderers themselves write into mount elements, which fires
 * the observer, so a scan that rediscovered everything would loop.
 */
export function scanMounts(
  registry: MountRegistry,
  options: ScanOptions,
  root: ParentNode = document
): MountRecord[] {
  const found: MountRecord[] = [];

  const add = (
    el: HTMLElement,
    mode: MountMode,
    eventId: string | null,
    creator: string | null,
    limit: number | null,
    legacy: boolean,
    autolink: boolean
  ): void => {
    if (registry.byEl.has(el)) return;

    const record: MountRecord = {
      // Monotonic, NOT `list.length`: pruning shortens the list and a reused id would collide with
      // a record something else is still holding.
      id: registry.nextId++,
      mode: mode,
      el: el,
      eventId: eventId,
      creator: creator,
      limit: limit,
      legacy: legacy,
      autolink: autolink,
      preview: options.preview || readFlag(el, DT + "preview"),
      cta: readCta(el),
      theme: readTheme(el),
      bound: false,
      state: "pending",
      sid: null,
      card: null,
      items: null,
      impressed: false,
    };

    registry.byEl.set(el, record);
    registry.list.push(record);
    found.push(record);

    // Which mounts get a click handler, and why it is the MOUNT that gets it rather than the CTA.
    //
    // `button` always, including autolinked anchors: their handler tracks the click, and upgrades
    // it to the overlay when the href carried an event id. Card, list and bar mounts join it when
    // the organizer chose `data-thrivia-cta="overlay"`, which is the default.
    //
    // The listener goes on `record.el` and catches the CTA's click on the way up, rather than
    // being attached to the CTA itself. That is not a shortcut: a renderer's CTA is destroyed and
    // rebuilt by `swap()` on every state change, so a listener bound to the anchor would be
    // dropped on the next paint, while the mount element survives for the life of the mount. The
    // handler in `index.ts` decides whether the thing actually clicked was a CTA.
    //
    // The `page` surface is excluded: it renders an iframe that runs the whole checkout itself, so
    // an overlay on top of it would be the same checkout twice.
    //
    // BOUND_ATTR, not `record.bound`, is the authority on whether this ELEMENT already carries a
    // handler, and the distinction is the whole of this guard. `byEl` is keyed by element and is
    // emptied for a detached one by `pruneMounts`, so an ordinary single-page-application node move
    // (detach, re-attach) produces a brand new record for a node that never lost the listener from
    // its first registration. Binding again gave it TWO: two overlays opened per click and two
    // `checkout_open` beacons per click, on nothing more exotic than a host framework reparenting a
    // section. The stamp is an attribute, so unlike the registry it survives the detach, which is
    // exactly why it is the thing to ask. The autolink branch already consulted it; the two
    // selectors above never did.
    // `bar` is excluded with `page`: its CTA lives on `document.body`, outside this mount, so a
  // listener here would never hear it. The satellite binds its own, and stamping the handover
  // token on an element we do not actually claim would weaken the one thing that token means.
  if (mode === "button" || (record.cta === "overlay" && mode !== "page" && mode !== "bar")) {
      if (el.hasAttribute(BOUND_ATTR)) record.bound = true;
      else bind(record, registry, options);
    }
  };

  // 1. The SDK's own mounts.
  const mounts = root.querySelectorAll<HTMLElement>(MOUNT_SELECTOR);
  for (let i = 0; i < mounts.length; i++) {
    const el = mounts[i];
    const creator = readAttr(el, DT + "creator");
    if (creator) {
      // The mode attribute is ignored for a creator mount: `list` is the only creator surface, and
      // honouring `data-thrivia-mode="card"` here would ask the wrong endpoint for the wrong shape.
      add(el, "list", null, creator, readLimit(el), false, false);
      continue;
    }
    const eventId = readAttr(el, DT + "event");
    if (eventId) add(el, readMode(el, "card"), eventId, null, null, false, false);
  }

  // 2. MUST-CLAIM: the legacy snippet, by its own selector and its own attribute.
  const legacy = root.querySelectorAll<HTMLElement>(LEGACY_SELECTOR);
  for (let i = 0; i < legacy.length; i++) {
    const el = legacy[i];
    const eventId = readAttr(el, DT + "event-id");
    if (eventId) add(el, "button", eventId, null, null, true, false);
  }

  // 3. Opt-in autolink over links that were already on the page.
  if (options.autolink) {
    const anchors = root.querySelectorAll<HTMLAnchorElement>("a[href]");
    for (let i = 0; i < anchors.length; i++) {
      const anchor = anchors[i];
      // Never the baked permalink inside one of our own mounts: that anchor IS the fallback the
      // whole failure posture depends on, and upgrading it would put our click handler on the one
      // element that has to keep working when everything else has failed.
      if (anchor.closest(CLAIMED_SELECTOR)) continue;
      if (anchor.hasAttribute(BOUND_ATTR)) continue;
      if (!isThriviaLink(anchor)) continue;
      add(anchor, "button", linkEventId(anchor), null, null, false, true);
    }
  }

  return found;
}

/**
 * Attaches the click handler and stamps the element.
 *
 * The stamp goes on in the same statement pair as the listener, deliberately: an element that is
 * bound but not stamped gets two handlers (ours and the shim's), one opening a tab and the other an
 * overlay, and an element stamped but not bound gets none.
 */
function bind(record: MountRecord, registry: MountRegistry, options: ScanOptions): void {
  const el = record.el;
  el.addEventListener("click", function (event: Event): void {
    // The LIVE record for this element, looked up at click time rather than closed over. The
    // listener outlives the record whenever a host detaches and re-attaches the node: the stamp
    // stops us binding a second handler, so this one handler has to keep serving whatever record
    // the element has now, or a re-attached CTA would open checkout against the sid and the card
    // data of the mount it used to be. Falls back to the record it was made for, which is what an
    // autolinked anchor that was never re-registered still has.
    options.onClick(registry.byEl.get(el) || record, event as MouseEvent);
  });
  el.setAttribute(BOUND_ATTR, "");
  record.bound = true;
}
